import { useState, useCallback, useEffect } from 'react';
import { useForm } from '@mantine/form';
import {
  getInterestSuggestions,
  registerSubscriberAndCreateNewsletter,
  SubscriberRegistration
} from '../../../services/api';
import { useAltcha } from './useAltcha';

type FormData = SubscriberRegistration;

const LOADING_DURATION = 5000;

export const useNewsletterForm = () => {
  const [error, setError] = useState<string | null>(null);
  const [interestSuggestions, setInterestSuggestions] = useState<string[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [modalMode, setModalMode] = useState<'newsletter_generated' | 'doi_started'>(
    'newsletter_generated'
  );
  const [additionalChips, setAdditionalChips] = useState<string[]>([]);
  const [loadedInterestData, setLoadedInterestData] = useState<string[]>([]);
  const [isPositionConfirmed, setIsPositionConfirmed] = useState(false);

  const {
    altchaPayload,
    isAltchaVerified,
    altchaError,
    handleAltchaVerified,
    handleAltchaError,
    resetAltcha
  } = useAltcha();

  const form = useForm<FormData>({
    initialValues: {
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      industry: '',
      position: '',
      interests: [],
      additionalInterests: '',
      consent: false
    },
    validate: {
      email: value => (/^\S+@\S+$/.test(value) ? null : 'Ungültige E-Mail-Adresse'),
      firstName: value => (value.length < 2 ? 'Name muss mindestens 2 Zeichen lang sein' : null),
      lastName: value => (value.length < 2 ? 'Name muss mindestens 2 Zeichen lang sein' : null),
      phoneNumber: value => {
        if (!value) return null;

        const isValidChars = /^(\+)?[0-9/]+$/.test(value);
        const hasNoSlashAtEnds = !value.startsWith('/') && !value.endsWith('/');

        const firstSlashIndex = value.indexOf('/');
        const isFirstSlashValid = firstSlashIndex === -1 || firstSlashIndex >= 3;

        const lastSlashIndex = value.lastIndexOf('/');
        const isLastSlashValid = lastSlashIndex === -1 || value.length - lastSlashIndex - 1 >= 3;

        if (isValidChars && hasNoSlashAtEnds && isFirstSlashValid && isLastSlashValid) {
          return null;
        }

        return 'Ungültige Telefonnummer';
      },
      industry: value => (!value ? 'Bitte wählen Sie eine Branche' : null),
      position: value => (!value ? 'Bitte geben Sie Ihre Position ein' : null),
      interests: () => {
        const hasGeneratedInterests = form.values.interests.length > 0;
        const hasAdditionalInterests = additionalChips.length > 0;

        if (!hasGeneratedInterests && !hasAdditionalInterests) {
          return 'Bitte wählen Sie mindestens ein Interesse aus oder fügen Sie ein eigenes hinzu';
        }

        return null;
      },
      consent: value => (value ? null : 'Bitte akzeptieren Sie die Datenschutzerklärung')
    },
    validateInputOnBlur: false,
    validateInputOnChange: false,
    clearInputErrorOnChange: true
  });

  useEffect(() => {
    if (!isLoadingSuggestions && loadedInterestData.length > 0) {
      setInterestSuggestions(loadedInterestData);
      setLoadedInterestData([]);
    }
  }, [isLoadingSuggestions, loadedInterestData]);

  const fetchSuggestions = useCallback(async () => {
    try {
      const { industry, position } = form.values;

      if (!position || !industry) {
        return;
      }

      setIsLoadingSuggestions(true);
      const startTime = Date.now();

      const suggestions = await getInterestSuggestions(industry, position);
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, LOADING_DURATION - elapsed);

      setLoadedInterestData(suggestions);

      setTimeout(() => {
        setIsLoadingSuggestions(false);
      }, remainingTime);
    } catch (error) {
      console.error('Fehler beim Laden der Interessenvorschläge:', error);

      setTimeout(() => {
        setIsLoadingSuggestions(false);
        setLoadedInterestData([]);
      }, LOADING_DURATION);
    }
  }, [form.values.industry, form.values.position]);

  const confirmPosition = useCallback(() => {
    if (form.values.position.trim() !== '') {
      setIsPositionConfirmed(true);
      fetchSuggestions();
    }
  }, [form.values.position, fetchSuggestions]);

  const resetInterestAndPosition = useCallback(() => {
    form.setFieldValue('position', '');
    form.setFieldValue('interests', []);
    setInterestSuggestions([]);
    setLoadedInterestData([]);
    setIsPositionConfirmed(false);
  }, [form]);

  const handleSubmit = async (values: FormData) => {
    setError(null);
    setIsSubmitting(true);

    if (!isAltchaVerified || !altchaPayload) {
      setError('Bitte warten Sie, bis die Sicherheitsprüfung abgeschlossen ist.');
      setIsSubmitting(false);
      return;
    }

    setShowProcessingModal(true);

    try {
      const updatedValues = {
        ...values,
        additionalInterests: additionalChips.join(', ')
      };

      const subscriberData: SubscriberRegistration = {
        ...updatedValues,
        firstName: updatedValues.firstName,
        lastName: updatedValues.lastName,
        industry: updatedValues.industry,
        originalIndustry: updatedValues.originalIndustry,
        position: updatedValues.position,
        altcha: altchaPayload
      };

      const response = await registerSubscriberAndCreateNewsletter(subscriberData);

      setShowProcessingModal(false);

      if (response.message && response.message.includes('bestätigen Sie Ihre E-Mail-Adresse')) {
        setModalMode('doi_started');
      } else {
        setModalMode('newsletter_generated');
      }

      setShowSuccessModal(true);

      form.reset();
      setAdditionalChips([]);
      resetAltcha();
    } catch (error) {
      setShowProcessingModal(false);
      setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddChip = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (form.values.additionalInterests === undefined) {
      return;
    }
    if (event.key === 'Enter' && form.values.additionalInterests.trim() !== '') {
      event.preventDefault();
      setAdditionalChips([...additionalChips, form.values.additionalInterests.trim()]);
      form.setFieldValue('additionalInterests', '');
      form.setFieldError('interests', null);
    }
  };

  const handleRemoveChip = (chipToRemove: string) => {
    const newChips = additionalChips.filter(chip => chip !== chipToRemove);
    setAdditionalChips(newChips);
  };

  return {
    form,
    error,
    interestSuggestions,
    setInterestSuggestions,
    isLoadingSuggestions,
    isSubmitting,
    showSuccessModal,
    setShowSuccessModal,
    showProcessingModal,
    setShowProcessingModal,
    modalMode,
    additionalChips,
    setAdditionalChips,
    isPositionConfirmed,
    setIsPositionConfirmed,
    confirmPosition,
    resetInterestAndPosition,
    handleSubmit,
    handleAddChip,
    handleRemoveChip,
    altchaError,
    isAltchaVerified,
    handleAltchaVerified,
    handleAltchaError
  };
};
